import { Weapon } from '../Weapon.js';
import { WEAPONS } from '../../../../config/constants.js';

/**
 * Orbit Ward — persistent shards that circle the player and damage the
 * boss on contact. Unlike every other weapon here, it isn't timer-fired:
 * it maintains a standing set of shards (count scales with level) and
 * overrides `update()` instead of `fire()` so those shards get spawned
 * once and then tracked, not re-fired every fire-interval.
 *
 * Shards are `hits: Infinity`-ish (a very large number — see below) with
 * a hit-cooldown, so they tick damage into the boss repeatedly as they
 * spin through it rather than a normal bullet's one-and-done hit. They orbit
 * well outside the player (a real defensive ring, not a melee hug), though
 * unlike some other weapons they no longer block incoming boss bullets
 * (`blocksBullets: false` in config/constants.js — that cross-pool check
 * still lives in Game.js's update loop for any weapon that wants it).
 * Shards also carry `knocksBack`, which CombatSystem._checkCollisions uses
 * to shove struck minions away (never the boss — its position is driven by
 * its own attack patterns, same reasoning as the dash-attack knockback skip).
 *
 * At level 4+, shards also tag whatever they hit with a stacking
 * damage-over-time (CombatSystem._applyDotStack/_updateDotEffects). At
 * level 8 (capstone), each shard bursts a small shockwave every time it
 * completes a full rotation.
 */
export class OrbitWard extends Weapon {
  constructor() {
    super(WEAPONS.orbitWard);
    this._shards = [];
  }

  /**
   * Deactivates the standing shard ring. Needed when the weapon instance is
   * removed mid-run (Harmonics fusion, CombatSystem.fuseWeapons) — the
   * shards' 1e9 lifetime means they'd otherwise orbit ownerless forever.
   */
  dispose() {
    for (const shard of this._shards) shard.active = false;
    this._shards = [];
  }

  /**
   * No timer — shards persist once spawned, so this replaces fire()'s role
   * entirely. Its "fire rate" instead is how often a standing shard can
   * re-hit the same target (hitCooldownDuration), which Projectile.update()
   * counts down in real time — so unlike every other weapon (which just
   * gets a scaled-up dt, see CombatSystem.update), Orbit Ward has to fold
   * the player's fire-rate multipliers into that cooldown itself, or it's
   * the one weapon "+fire rate" effects silently do nothing for.
   */
  update(_dt, origin, _target, pool) {
    this._shards = this._shards.filter((s) => s.active);

    const fireRateMultiplier = (origin.surgeFireRateMultiplier ?? 1)
      * (origin.fireRateMultiplier ?? 1);
    const hitCooldownDuration = this.config.hitCooldownDuration / fireRateMultiplier;

    const desiredCount = 5 + Math.floor(this.level / 2); // 5 @ lvl1 (was 1), up to 9 @ lvl8
    while (this._shards.length < desiredCount) {
      const shard = pool.fire({
        x: origin.x,
        y: origin.y,
        radius: this.config.projectileRadius,
        damage: this.config.damage + this.level,
        hits: 1e9, // functionally infinite — despawns with the run, not on hit count
        hitCooldownDuration,
        lifetime: 1e9,
        color: this.config.color,
        blocksBullets: this.config.blocksBullets,
        knocksBack: this.config.knocksBack,
        knockbackForce: this.config.knockbackForce,
        orbitCenter: origin,
        // Ring widens slightly with level so higher-level Orbit Ward feels bigger
        // (and shields more area) on top of adding more shards.
        orbitRadius: this.config.orbitRadius + this.level,
        orbitAngle: 0, // re-spaced below, along with every other shard
        orbitAngularSpeed: this.config.angularSpeed,
      });
      this._shards.push(shard);
    }

    // Re-sync every shard (not just newly spawned ones) to the current
    // level's radius and an even angle spacing every frame. Without this,
    // a level-up that grows desiredCount only set radius/angle on the
    // *new* shard above — the rest of the ring kept whatever radius/spacing
    // it spawned with, so the ring visibly desynced (mismatched radii,
    // uneven gaps) the moment a level-up added a shard mid-ring. Angle is
    // anchored to shard 0's own angle (which Projectile.update() keeps
    // advancing every frame via orbitAngularSpeed) rather than reset to 0,
    // so the ring keeps spinning smoothly instead of snapping in place.
    if (this._shards.length > 0) {
      const anchorAngle = this._shards[0].orbitAngle;
      this._shards.forEach((shard, i) => {
        shard.orbitRadius = this.config.orbitRadius + this.level;
        shard.orbitAngle = anchorAngle + (i / this._shards.length) * Math.PI * 2;
        // Level 4+: shards tag whatever they hit with a stacking DoT (see
        // CombatSystem._applyDotStack) — same field set every frame like
        // orbitRadius/orbitAngle above, so it applies retroactively to
        // shards that spawned before the ring reached level 4.
        shard.appliesDot = this.level >= 4;
        shard.dotDamagePerTick = 2 + Math.floor(this.level / 4); // 3 @ lvl4, 4 @ lvl8
        // Also kept live so a fire-rate change mid-run (ultimate surge,
        // Rapid Fire pickup, Overclock pet) speeds up shards already on
        // the ring, not just ones spawned after the change.
        shard.hitCooldownDuration = hitCooldownDuration;
      });
    }

    // Level 8 capstone: each shard fires a small shockwave burst every time
    // it completes a full rotation — detected by watching its wrapped angle
    // wrap back around (this frame's mod is smaller than last frame's).
    if (this.level >= 8) {
      this._shards.forEach((shard) => {
        const angleMod = ((shard.orbitAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
        if (shard._owPrevAngleMod !== undefined && angleMod < shard._owPrevAngleMod) {
          pool.fire({
            x: shard.x,
            y: shard.y,
            radius: 30,
            damage: this.config.damage + this.level,
            hits: 1,
            lifetime: 0.15,
            color: this.config.color,
            style: 'ring',
          });
        }
        shard._owPrevAngleMod = angleMod;
      });
    }
  }
}
